import { useEffect, useState } from 'react';

/**
 * The manual, in the product.
 *
 * Written as a page rather than a docs site because the questions it answers
 * arrive *while* someone is using the app — "why is this cell a dash", "why did
 * my summary lose a sentence", "why did the simple model win". Sending them to
 * a separate site to find out is how a deliberate design decision gets filed as
 * a bug.
 *
 * The `onNavigate` prop matters: every explanation ends somewhere the reader can
 * act, so the page teaches by sending people into the product rather than by
 * describing it at length.
 */

type Page =
  | 'sources'
  | 'overview'
  | 'explore'
  | 'data'
  | 'pivot'
  | 'forecast'
  | 'dashboard'
  | 'reports'
  | 'looker'
  | 'templates'
  | 'powerbi'
  | 'portals'
  | 'client-reports'
  | 'settings'
  | 'ai'
  | 'help';

interface Section {
  readonly id: string;
  readonly label: string;
}

const SECTIONS: readonly Section[] = [
  { id: 'what', label: 'What this is' },
  { id: 'journey', label: 'The journey' },
  { id: 'features', label: 'Every feature' },
  { id: 'walkthrough', label: 'Worked examples' },
  { id: 'value', label: 'What it saves you' },
  { id: 'cases', label: 'Use cases' },
  { id: 'quirks', label: 'Not bugs' },
  { id: 'faq', label: 'FAQ' },
  { id: 'limits', label: 'Honest limits' },
];

export function HowItWorks({
  onStart,
  onNavigate,
}: {
  onStart: () => void;
  onNavigate?: (page: Page) => void;
}) {
  const [active, setActive] = useState('what');

  // Highlight the section currently in view. `rootMargin` biases the trigger
  // toward the top of the viewport so the nav updates when a heading arrives,
  // not when it is halfway down the screen.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActive(entry.target.id);
        }
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
    );

    for (const section of SECTIONS) {
      const element = document.getElementById(section.id);
      if (element !== null) observer.observe(element);
    }
    return () => observer.disconnect();
  }, []);

  const go = (page: Page) => () => onNavigate?.(page);

  return (
    <div className="page guide">
      <header className="guide-hero">
        <span className="badge accent">Guide</span>
        <h1>How DataVantage works</h1>
        <p>
          Point it at a spreadsheet or a marketing platform. It reads every column, works out what
          each one is, computes what can honestly be computed, and shows you the working. Ten minutes
          from here to a dashboard you can send to a client.
        </p>
        <div className="row">
          <button className="primary" onClick={onStart}>
            Add your first source
          </button>
          <a href="#journey">
            <button className="ghost">See the five steps</button>
          </a>
        </div>
      </header>

      <nav className="guide-nav" aria-label="On this page">
        {SECTIONS.map((section) => (
          <a
            key={section.id}
            href={`#${section.id}`}
            aria-current={active === section.id ? 'true' : undefined}
          >
            {section.label}
          </a>
        ))}
      </nav>

      {/* ------------------------------------------------------------ what */}

      <section id="what" className="guide-section">
        <h2>What this is, in one paragraph</h2>
        <p className="lede">
          Most analytics tools ask you to already know what you want to see. This one starts from the
          data: it profiles every column, tells you what it found, and builds the first dashboard for
          you. Then you can explore, forecast, schedule, share, and export — including straight into
          your own Looker Studio account.
        </p>

        <div className="pillars">
          <article>
            <div className="pillar-glyph" aria-hidden>
              ✓
            </div>
            <h3>Every number is computed</h3>
            <p>
              Nothing on any screen is sample data, a placeholder, or a guess. If a figure cannot be
              computed honestly, the app leaves it out and says why.
            </p>
          </article>
          <article>
            <div className="pillar-glyph" aria-hidden>
              ⌕
            </div>
            <h3>Every number is traceable</h3>
            <p>
              Each finding carries the evidence behind it — the statistic, the column, the value. You
              can check any claim without leaving the page.
            </p>
          </article>
          <article>
            <div className="pillar-glyph" aria-hidden>
              ⚖
            </div>
            <h3>The app tells you when it is unsure</h3>
            <p>
              A forecast that cannot be trusted says so. A comparison against a half-finished month is
              labelled. An AI sentence that quoted an invented number is removed before you see it.
            </p>
          </article>
        </div>
      </section>

      {/* --------------------------------------------------------- journey */}

      <section id="journey" className="guide-section">
        <h2>The journey, start to finish</h2>
        <p className="lede">
          Five steps. Steps 1 and 2 take about a minute; everything after is optional and reversible.
        </p>

        <div className="flow">
          {[
            {
              n: 1,
              title: 'Connect',
              body: 'Drop a file on the AI analytics page — CSV, Excel, JSON, XML, PDF or a log — or connect Search Console, BigQuery, Google Sheets or Meta Ads.',
              action: 'ai' as const,
              cta: 'Open AI analytics',
            },
            {
              n: 2,
              title: 'It reads the data',
              body: 'Columns are typed, IDs are spotted and excluded from metrics, gaps and duplicates are counted.',
              action: 'data' as const,
              cta: 'See the table',
            },
            {
              n: 3,
              title: 'Read the overview',
              body: 'Headline figures, the findings it computed, a data-quality report, and the charts it recommends.',
              action: 'overview' as const,
              cta: 'Open Overview',
            },
            {
              n: 4,
              title: 'Dig in',
              body: 'Group, filter, pivot, forecast. Build a dashboard of tiles that each query the data directly.',
              action: 'explore' as const,
              cta: 'Start exploring',
            },
            {
              n: 5,
              title: 'Deliver',
              body: 'Export a PDF, share a read-only link, push to Looker Studio, or schedule it to refresh and email itself.',
              action: 'looker' as const,
              cta: 'Looker Studio',
            },
          ].map((step) => (
            <div className="flow-step" key={step.n}>
              <div className="flow-mark">{step.n}</div>
              <div className="flow-body">
                <h3>{step.title}</h3>
                <p>{step.body}</p>
                {onNavigate !== undefined && (
                  <button className="ghost tiny" onClick={go(step.action)}>
                    {step.cta} →
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <Behind title="What happens the moment you upload">
          <p>
            The file is parsed <strong>once, on the server</strong>. Both of the projects this replaced
            parsed it twice — once in the browser and once on the server — and the two disagreed about
            what a column was, which silently broke every insight downstream.
          </p>
          <ol>
            <li>The file is read and headers are cleaned — blanks named, duplicates renamed.</li>
            <li>
              Every cell is coerced: currency symbols, thousands separators, accounting negatives like
              <code>(450.25)</code>, and null-ish sentinels such as <code>N/A</code>.
            </li>
            <li>
              Each column is typed as <em>numeric</em>, <em>categorical</em>, <em>date</em>,{' '}
              <em>boolean</em> or <em>empty</em>, and flagged as an identifier if it looks like a key.
            </li>
            <li>Scorecards, findings, chart recommendations and a quality report are derived.</li>
            <li>A report is saved automatically, so you never lose the analysis.</li>
          </ol>
        </Behind>
      </section>

      {/* -------------------------------------------------------- features */}

      <section id="features" className="guide-section">
        <h2>Every feature, and what it does for you</h2>

        <Feature
          glyph="✦"
          name="AI analytics"
          summary="Drop a file in; get the whole dashboard back. Nothing to configure."
          onOpen={onNavigate === undefined ? undefined : go('ai')}
        >
          <p>
            The rest of the app gives you tools. This page gives you the finished thing: upload a file
            and it decides what the data is about, then builds the dashboard around that decision.
          </p>
          <ul>
            <li>
              <strong>More formats.</strong> CSV and Excel, plus JSON, NDJSON, XML, PDF, and log or
              plain-text files. A JSON envelope is unwrapped to find the records; an XML document's
              repeating element becomes the row; a PDF's table is rebuilt from where the text sits on
              the page.
            </li>
            <li>
              <strong>It picks the headline itself.</strong> Which column is the measure, which is
              worth slicing by, which date is the business event rather than a metadata timestamp.
              Every choice is visible, and every block says why it is there.
            </li>
            <li>
              <strong>It answers "why", not just "what".</strong> Alongside the charts it attributes
              the period-on-period change to the groups that caused it — a name, not a shrug.
            </li>
            <li>
              <strong>Everything cross-filters.</strong> Click a bar, a slice or a heatmap cell and
              the whole page re-renders against that filter in one request.
            </li>
          </ul>
          <p className="benefit">
            <strong>Why it helps:</strong> the gap between having a file and having a dashboard is
            about a minute, and nothing on the page was chosen by someone who had not seen your data.
          </p>
        </Feature>

        <Feature
          glyph="⇥"
          name="Sources"
          summary="Files and live platforms, in the same shape."
          onOpen={onNavigate === undefined ? undefined : go('sources')}
        >
          <p>
            Upload <code>.csv</code>, <code>.tsv</code>, <code>.txt</code>, <code>.xlsx</code> or{' '}
            <code>.xlsm</code>, or connect a platform:
          </p>
          <ul>
            <li>
              <strong>Google Search Console</strong> — clicks, impressions, CTR and position by date,
              query, page, country or device.
            </li>
            <li>
              <strong>BigQuery</strong> — any table or query result, with the date range passed as
              bound parameters rather than pasted into SQL.
            </li>
            <li>
              <strong>Google Sheets</strong> — any tab, with numbers arriving as numbers and dates as
              dates rather than serial numbers.
            </li>
            <li>
              <strong>Meta Ads</strong> — spend, impressions, clicks and conversions by campaign, ad
              set or ad.
            </li>
          </ul>
          <p className="benefit">
            <strong>Why it helps:</strong> a spreadsheet and a BigQuery table get identical profiling,
            charts and forecasting. You learn the tool once.
          </p>
        </Feature>

        <Feature
          glyph="◈"
          name="Overview"
          summary="The first thing you see, built without you asking."
          onOpen={onNavigate === undefined ? undefined : go('overview')}
        >
          <ul>
            <li>
              <strong>Scorecards</strong> — headline figures, with a trend arrow only when there is a
              date column and enough points to measure one.
            </li>
            <li>
              <strong>Findings</strong> — concentration, correlation, outliers, variability and trend,
              each with the evidence that produced it.
            </li>
            <li>
              <strong>Data quality</strong> — completeness, duplicate rows, constant columns, and a
              remedy for each problem found.
            </li>
            <li>
              <strong>Recommended charts</strong> — with the reason each was suggested.
            </li>
            <li>
              <strong>Written summary</strong> — optional, and checked before you see it.
            </li>
          </ul>
          <p className="benefit">
            <strong>Why it helps:</strong> the first useful read of a new dataset takes seconds, not an
            afternoon of pivot tables.
          </p>
        </Feature>

        <Feature
          glyph="⌕"
          name="Explore"
          summary="Ask a question, switch how you look at the answer."
          onOpen={onNavigate === undefined ? undefined : go('explore')}
        >
          <p>
            Pick a dimension to group by, an aggregation, and a measure. Switch the same answer between
            a bar chart, a line, a share chart or a table without re-running anything.
          </p>
          <ul>
            <li>Bucket dates by day, week, month, quarter or year.</li>
            <li>
              Seven aggregations: sum, average, count, distinct count, min, max and median — and the
              chart always says which one it used.
            </li>
            <li>
              Top groups are kept and the rest folded into an “Other” bar, so the total still
              reconciles.
            </li>
          </ul>
          <p className="benefit">
            <strong>Why it helps:</strong> a chart can never mislabel itself. The aggregation is part of
            the question, not a hidden default.
          </p>
        </Feature>

        <Feature
          glyph="▦"
          name="Pivot"
          summary="Cross-tabulate two dimensions, with honest margins."
          onOpen={onNavigate === undefined ? undefined : go('pivot')}
        >
          <p>
            Rows down one side, columns across the top, a measure in the cells. Two details make it
            trustworthy:
          </p>
          <ul>
            <li>
              An empty intersection shows a <strong>dash, never a zero</strong>. “No orders in the North
              in Q3” and “orders worth zero” are different facts.
            </li>
            <li>
              Margins are computed from the underlying values, not by adding up the cells. The row
              margin of an average pivot is the true average — not the sum of the cell averages, which
              measures nothing.
            </li>
          </ul>
          <p className="benefit">
            <strong>Why it helps:</strong> the totals reconcile. You can paste them into a report without
            checking them by hand.
          </p>
        </Feature>

        <Feature
          glyph="⤳"
          name="Forecast"
          summary="A real model — and an honest answer about whether to trust it."
          onOpen={onNavigate === undefined ? undefined : go('forecast')}
        >
          <p>The model has three parts, the way Prophet popularised:</p>
          <ul>
            <li>
              <strong>A trend that can bend.</strong> The app looks for points where the underlying rate
              genuinely changed and reports them, rather than fitting one straight line through a series
              that changed direction.
            </li>
            <li>
              <strong>Seasonality it detected, not assumed.</strong> A weekly or yearly cycle is only
              included if it explains real variation and you have enough history to see it twice.
            </li>
            <li>
              <strong>A widening band.</strong> The prediction interval grows with distance, because the
              uncertainty does.
            </li>
          </ul>
          <p>
            Then the important part: press <strong>Backtest against baselines</strong>. The app refits
            the model at several points in the past, scores it on data it never saw, and does the same
            for simple baselines like “this week looks like last week”.
          </p>
          <p className="benefit">
            <strong>Why it helps:</strong> sometimes the simple baseline wins — and you find out before
            you build a plan on the forecast, not after. It also reports <em>coverage</em>: the share of
            real values that landed inside the band. A 95% band covering 60% of reality is not a 95%
            band.
          </p>
        </Feature>

        <Feature
          glyph="▤"
          name="Dashboard builder"
          summary="Drag, resize, configure. It saves itself."
          onOpen={onNavigate === undefined ? undefined : go('dashboard')}
        >
          <p>
            Seven tile types — scorecard, bar, line, share, table, pivot and forecast. Drag to move,
            drag the corner to resize, click to configure. Changes save automatically about a second
            after you stop.
          </p>
          <p>
            A workspace-wide filter applies to every tile at once, and each tile queries the dataset
            through the same path the Explore tab uses — so two tiles can never disagree about the same
            number.
          </p>
          <p className="benefit">
            <strong>Why it helps:</strong> a client-ready board in a few minutes, and it is still there
            tomorrow.
          </p>
        </Feature>

        <Feature
          glyph="↗"
          name="Looker Studio"
          summary="Get the data into your own Google account."
          onOpen={onNavigate === undefined ? undefined : go('looker')}
        >
          <ol>
            <li>
              <strong>Publish</strong> writes the analysed data to a new Google Sheet in your own Drive.
            </li>
            <li>
              <strong>Open in Looker Studio</strong> creates a report in your account, already bound to
              that sheet with every field typed correctly.
            </li>
          </ol>
          <p>
            If your administrator has set a template report, that one click copies a finished dashboard
            — charts and all — and points it at your data.
          </p>
          <p className="benefit">
            <strong>Why it helps:</strong> the report lives in your Google account, under your control,
            shareable with anyone who already has Looker Studio access.
          </p>
        </Feature>

        <Feature
          glyph="⊞"
          name="Template marketplace"
          summary="Pre-built dashboards for your industry. One click to a finished report."
          onOpen={onNavigate === undefined ? undefined : go('templates')}
        >
          <p>
            Choose from templates built for e-commerce, SaaS, SEO, real estate, restaurants, law firms,
            marketing agencies, and finance. Each template knows which columns matter, which charts to
            build, and what calculated fields to add.
          </p>
          <ul>
            <li>
              <strong>Auto-matching.</strong> Upload your data and the app scores every template against
              your column names. The best match rises to the top with a confidence percentage.
            </li>
            <li>
              <strong>Field mapping.</strong> The template maps its expected fields to your actual columns
              — "revenue" matches "sales", "total", or "amount". You see the mapping before applying.
            </li>
            <li>
              <strong>One-click Looker Studio.</strong> Applying a template produces a finished Looker
              Studio dashboard with the right charts, measures, and calculated fields already configured.
            </li>
          </ul>
          <p className="benefit">
            <strong>Why it helps:</strong> instead of spending an hour building a dashboard from scratch,
            you start with one that already knows what your industry cares about.
          </p>
        </Feature>

        <Feature
          glyph="⚡"
          name="Power BI"
          summary="Push your analysis straight into Power BI."
          onOpen={onNavigate === undefined ? undefined : go('powerbi')}
        >
          <p>
            Three paths into Power BI, depending on how automated you want to be:
          </p>
          <ul>
            <li>
              <strong>Template export</strong> — a JSON manifest with pre-built DAX measures (ROAS, CTR,
              AOV), field types, and suggested visuals. Import it alongside the CSV for a ready-made report.
            </li>
            <li>
              <strong>Push dataset</strong> — data is pushed directly to a Power BI workspace via the REST
              API. The dataset appears in your workspace ready for report building.
            </li>
            <li>
              <strong>Optimised CSV</strong> — dates as ISO 8601, numbers with dot separators, clean column
              names. Power BI's importer never guesses wrong.
            </li>
          </ul>
          <p className="benefit">
            <strong>Why it helps:</strong> enterprises live in Power BI. Being the bridge from raw data to
            a configured Power BI report in 30 seconds is the fastest path to a deliverable.
          </p>
        </Feature>

        <Feature
          glyph="⎔"
          name="Client portals"
          summary="Branded dashboards your clients can view without an account."
          onOpen={onNavigate === undefined ? undefined : go('portals')}
        >
          <p>
            Create a portal for each client — a custom URL with your company name, logo, and colours.
            The client sees only the reports you assign. DataVantage is invisible.
          </p>
          <ul>
            <li>
              <strong>Token-based access.</strong> Each client gets a unique link. No password to remember,
              no account to create. The link expires and can be revoked at any time.
            </li>
            <li>
              <strong>Granular permissions.</strong> Control whether the client can export data, see raw
              rows, or use filters and exploration.
            </li>
            <li>
              <strong>Access tracking.</strong> See when each client last viewed their portal and how many
              times they have accessed it.
            </li>
          </ul>
          <p className="benefit">
            <strong>Why it helps:</strong> agencies currently pay $200–800/month for tools like DashThis
            or ReportGarden for less capability. This replaces them.
          </p>
        </Feature>

        <Feature
          glyph="✉"
          name="Client reports"
          summary="Branded PDF reports, composed and sent automatically."
          onOpen={onNavigate === undefined ? undefined : go('client-reports')}
        >
          <p>
            Turn any analysis into a polished client deliverable. The report includes a cover page,
            executive summary, key metrics with trends, findings, and recommendations — all styled with
            your branding.
          </p>
          <ul>
            <li>
              <strong>Preview before sending.</strong> See exactly what the client will receive in an
              inline preview. Download the HTML or send it directly via email.
            </li>
            <li>
              <strong>AI-written summary.</strong> The executive summary is generated from the analysis
              findings, with every figure traced back to the data.
            </li>
            <li>
              <strong>Your brand, not ours.</strong> Company name, logo, accent colour, and footer text
              all come from your workspace settings. The "Powered by DataVantage" line is removed on
              paid plans.
            </li>
          </ul>
          <p className="benefit">
            <strong>Why it helps:</strong> what used to take an hour of copy-pasting into a slide deck
            now takes one click. The report is always accurate because every figure is computed, not typed.
          </p>
        </Feature>

        <Feature
          glyph="⟳"
          name="Scheduled refresh and digests"
          summary="It checks the data so you do not have to."
          onOpen={onNavigate === undefined ? undefined : go('settings')}
        >
          <p>
            Set a connected source to refresh daily, weekly or monthly. Each run re-fetches, re-analyses,
            and decides whether to email you.
          </p>
          <ul>
            <li>
              <strong>It emails when something moved</strong> or when a value was unexplained. The
              subject line carries the finding — “Clicks up 12.5%” — not a status.
            </li>
            <li>
              <strong>It stays quiet on a normal day.</strong> A daily “everything is fine” email is how
              the one that matters gets ignored.
            </li>
            <li>
              <strong>It always tells you when it broke.</strong> Silence must never mean failure.
            </li>
          </ul>
          <p className="benefit">
            <strong>Why it helps:</strong> you stop checking dashboards. The dashboard checks itself and
            interrupts you only when it should.
          </p>
        </Feature>

        <Feature
          glyph="⇗"
          name="Sharing and exports"
          summary="Four formats and a link that needs no account."
          onOpen={onNavigate === undefined ? undefined : go('settings')}
        >
          <ul>
            <li>
              <strong>PDF</strong> — a real document, with your logo and colours on the agency plan.
            </li>
            <li>
              <strong>Excel</strong> — a real workbook: summary, findings, quality, chart data and rows,
              on separate sheets.
            </li>
            <li>
              <strong>CSV</strong> — properly quoted, so a company name containing a comma does not
              shift every later column.
            </li>
            <li>
              <strong>Share link</strong> — read-only, no sign-in, optional passphrase and expiry,
              revocable at any time.
            </li>
          </ul>
          <p className="benefit">
            <strong>Why it helps:</strong> a share link is the fastest way to get a number in front of
            someone. It shows the findings and the summary — never the raw rows.
          </p>
        </Feature>
      </section>

      {/* ----------------------------------------------------- walkthrough */}

      <section id="walkthrough" className="guide-section">
        <h2>Two worked examples</h2>
        <p className="lede">
          Realistic runs from first upload to delivered result, with what the app is doing at each
          point.
        </p>

        <Walkthrough
          who="An SEO agency, Monday morning"
          goal="Find out why a client's traffic dipped, and set up a weekly check so nobody has to look again."
          steps={[
            {
              did: 'Connected Google Search Console and imported the last 90 days.',
              saw: '12,400 rows: date, query, page, country, device, clicks, impressions, CTR, position.',
              behind:
                'The connector paged through the API, converted CTR from a fraction to the percentage the Search Console UI shows, and profiled every column.',
            },
            {
              did: 'Read the Overview.',
              saw: '“Clicks are concentrated: the top 10 queries account for 61% of the total.” A quality warning that 340 rows have no country.',
              behind:
                'Concentration is measured across all groups, not the visible top few, so the figure does not change when you resize a chart.',
            },
            {
              did: 'Opened Forecast, bucketed by week, pressed Backtest.',
              saw: 'Weekly seasonality detected. Trend + seasonality beat every baseline, with 92% interval coverage.',
              behind:
                'The model was refitted at four past points and scored only on weeks it had never seen. In-sample fit was ignored, because it always improves with complexity.',
            },
            {
              did: 'Checked Unexpected points.',
              saw: 'Three days in the second week of the month, all far below expectation — and not a weekend pattern.',
              behind:
                'Anomalies are residuals against a model that already knows about weekly seasonality, so a normal Sunday dip is never flagged.',
            },
            {
              did: 'Built a four-tile dashboard, set the workspace logo, exported a PDF.',
              saw: 'A branded document with the agency name and no DataVantage mark.',
              behind: 'The logo is embedded in the PDF, so it renders offline and in email.',
            },
            {
              did: 'Created a weekly schedule with the client’s email on it.',
              saw: 'A confirmation that the next run is Monday at 06:00 UTC.',
              behind:
                'The Search Console credential was verified against Google before being stored encrypted. A bad credential fails now, not silently at 6am.',
            },
          ]}
          outcome="Monday mornings now start with an email that either says what changed or does not arrive. The three-day dip turned out to be a tracking outage — found in nine minutes."
        />

        <Walkthrough
          who="An ecommerce operations lead, month end"
          goal="Explain last month's margin and forecast the next quarter."
          steps={[
            {
              did: 'Uploaded a 60,000-row order export.',
              saw: 'Nine columns typed automatically. order_id and customer_id flagged as identifiers.',
              behind:
                'Identifier detection uses distinctness *and* value spread, so a genuine measure like units_sold is not mistaken for a key and silently dropped from your KPIs.',
            },
            {
              did: 'Looked at the scorecards.',
              saw: 'Total revenue, average order value, order count — and no “Average order_id”.',
              behind:
                'Identifier columns are excluded from metrics. One of the predecessor products displayed “Average ID: 12,240,450.5” on its dashboard.',
            },
            {
              did: 'Pivoted region against channel with average margin.',
              saw: 'A dash where a region never used a channel, and a margin labelled “Overall” rather than “Total”.',
              behind:
                'Averages are not additive, so the app refuses to call their margin a total. The number shown is the true average of every underlying row.',
            },
            {
              did: 'Forecast revenue for the next quarter.',
              saw: '“The trend last changed direction around 12 March; the projection follows the slope since then, not the whole history.”',
              behind:
                'The model found a genuine changepoint and extrapolates from the current regime — not from an average of two different ones.',
            },
            {
              did: 'Generated a written summary.',
              saw: 'Three paragraphs, plus a note that one sentence had been removed.',
              behind:
                'The model quoted a conversion rate that appears nowhere in the analysis. The sentence was deleted and the figure listed, because a caveat under a wrong number does not stop anyone believing it.',
            },
            {
              did: 'Sent a share link to the finance team.',
              saw: 'They opened it without an account and saw figures, findings and the summary.',
              behind:
                'The link exposes the report only. Not the 60,000 rows, not the other reports in the workspace, and not the ability to run a new query.',
            },
          ]}
          outcome="Month-end review went from a day of spreadsheet work to about forty minutes, and the margin question was answered with a number the team could check."
        />
      </section>

      {/* ----------------------------------------------------------- value */}

      <section id="value" className="guide-section">
        <h2>What it actually saves you</h2>

        <div className="value-grid">
          {[
            {
              before: 'Build pivot tables to see what is in a new file',
              after: 'Upload it and read the overview',
              gain: 'Hours → seconds',
            },
            {
              before: 'Export CSVs from four platforms and reconcile them by hand',
              after: 'Connect each one; they all land in the same shape',
              gain: 'A morning → a few clicks',
            },
            {
              before: 'Trust a trend line because it looks convincing',
              after: 'Backtest it against baselines and see the real error',
              gain: 'A wrong plan → an informed one',
            },
            {
              before: 'Rebuild the client deck every month',
              after: 'Branded PDF or a live share link, on a schedule',
              gain: 'Half a day, monthly → zero',
            },
            {
              before: 'Check dashboards in case something moved',
              after: 'Get an email only when something did',
              gain: 'Daily interruption → weekly signal',
            },
            {
              before: 'Rebuild the same report in Looker Studio by hand',
              after: 'Publish and open — fields already typed',
              gain: 'An afternoon → one click',
            },
          ].map((row) => (
            <div className="value-row" key={row.before}>
              <div className="value-before">{row.before}</div>
              <div className="value-arrow" aria-hidden>
                →
              </div>
              <div className="value-after">{row.after}</div>
              <div className="value-gain">{row.gain}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ----------------------------------------------------------- cases */}

      <section id="cases" className="guide-section">
        <h2>Who uses it, and for what</h2>

        <div className="grid">
          {[
            {
              who: 'SEO and marketing agencies',
              what: 'Client reporting at scale',
              how: 'Connect Search Console and Meta Ads per client, brand the exports with your own logo, schedule a weekly digest to the client directly.',
            },
            {
              who: 'Ecommerce and operations',
              what: 'Order, margin and inventory analysis',
              how: 'Upload the monthly export, pivot region against channel, forecast the next quarter, share the result with finance.',
            },
            {
              who: 'Analysts and data teams',
              what: 'Fast first-pass profiling',
              how: 'Point it at a BigQuery table before writing any SQL. See types, gaps, duplicates, correlations and outliers in one read.',
            },
            {
              who: 'Founders and small teams',
              what: 'A dashboard without a data team',
              how: 'Upload the spreadsheet you already keep. Get scorecards, a trend, and a forecast with an honest error estimate.',
            },
            {
              who: 'Consultants',
              what: 'Client deliverables',
              how: 'Analyse the data they sent, produce a branded PDF, and hand over a Looker Studio report that lives in their own account.',
            },
            {
              who: 'Anyone handed a mystery CSV',
              what: 'Working out what is in it',
              how: 'Upload it. The quality report alone usually answers the first three questions.',
            },
          ].map((card) => (
            <article className="card" key={card.who}>
              <header>
                <h3>{card.who}</h3>
              </header>
              <p style={{ margin: '0 0 8px', fontWeight: 600, fontSize: 13 }}>{card.what}</p>
              <p className="muted" style={{ fontSize: 13, margin: 0 }}>
                {card.how}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* ---------------------------------------------------------- quirks */}

      <section id="quirks" className="guide-section">
        <h2>Things that look like bugs and are not</h2>
        <p className="lede">
          Each of these is a deliberate decision. Knowing why will save you filing a ticket.
        </p>

        <div className="quirks">
          <Quirk symptom="A scorecard has no trend arrow">
            A trend is shown only when there is a date column and enough points to measure change. Where
            there is not, the card omits the arrow rather than printing <code>0.0%</code> — a
            placeholder that always renders teaches you to ignore the ones that mean something.
          </Quirk>

          <Quirk symptom="A pivot cell shows a dash">
            No rows fell at that intersection. Zero would mean rows fell there and summed to nothing.
            Those are different facts, and a grid that prints <code>0</code> for both cannot be
            reconciled against anything.
          </Quirk>

          <Quirk symptom="A total is labelled “Overall”">
            Totals are only additive for sums and counts. The margin of an average pivot is the average
            of all the underlying values — not the sum of the cell averages, which measures nothing. The
            label changes to say so.
          </Quirk>

          <Quirk symptom="The forecast refuses to run">
            A decomposable model needs about ten observations, and more parameters than data points is
            interpolation rather than forecasting. Rather than draw a confident line through six points,
            it falls back to a simpler model and tells you it did.
          </Quirk>

          <Quirk symptom="A sentence is missing from the AI summary">
            Generated prose is checked before you see it. Every number the model wrote is matched against
            the figures it was given; any sentence quoting an untraceable figure is removed and the
            figure listed. A model doing arithmetic of its own is doing the analysis layer's job without
            its tests.
          </Quirk>

          <Quirk symptom="A simple baseline beat the real model">
            The backtest scores every model on data it was not fitted to, including trivial baselines
            like “this week looks like last week”. Sometimes the simple one wins — and that is worth
            knowing. It is also why in-sample fit is never shown as an accuracy figure: it improves with
            complexity whether or not the model predicts anything.
          </Quirk>

          <Quirk symptom="A comparison says “partial period”">
            Four days of this month against a full previous month always looks like a collapse. The app
            counts how much of each period it actually observed and flags the comparison rather than
            publishing the false drop.
          </Quirk>
        </div>
      </section>

      {/* ------------------------------------------------------------- faq */}

      <section id="faq" className="guide-section">
        <h2>Frequently asked questions</h2>

        <div className="faq">
          {[
            {
              q: 'What file types can I upload, and how big?',
              a: 'CSV, TSV, TXT, XLSX and XLSM, up to 25 MB by default. Larger files are analysed up to a row cap and the app tells you the cap was reached rather than silently truncating.',
            },
            {
              q: 'Does my data leave the server?',
              a: 'Only if you ask it to. Uploads are parsed and stored server-side. Data is sent to an AI provider only when you press “Write summary”, and even then only the computed figures — never your raw rows. Publishing to Google Sheets sends the data to your own Drive.',
            },
            {
              q: 'Do I need an AI provider to use this?',
              a: 'No. Without one, summaries are composed directly from the computed findings — free, instant, offline, and incapable of inventing a figure. The panel always says which wrote what you are reading.',
            },
            {
              q: 'What happens to my connector credentials?',
              a: 'For a one-off import they are used for that request and never stored. For a scheduled refresh they must be kept, so they are encrypted with a key that is not in the database. If that key is not configured, scheduling is refused rather than storing anything in plaintext.',
            },
            {
              q: 'Can someone with a share link see my raw data?',
              a: 'No. A share link exposes the report’s figures, findings and summary. Not the rows, not your other reports, and not the ability to run a new query. You can add a passphrase, set an expiry, and revoke it at any time.',
            },
            {
              q: 'Why did my upload get rejected for a plan limit?',
              a: 'Limits are checked before the work rather than after, so you are not charged the wait for something that will be refused. The message names the plan, the limit and how much you have left.',
            },
            {
              q: 'How accurate is the forecast?',
              a: 'That is exactly what the backtest answers, and why it is a button rather than a footnote. It reports the average error in your own units and the share of real values that fell inside the prediction band. If those numbers are poor, the app says so instead of hiding it.',
            },
            {
              q: 'Can I use my own branding?',
              a: 'A company name, accent colour and footer note work on any plan. A logo and removing the DataVantage mark are agency-plan features, and apply to PDFs, emailed digests and the share page.',
            },
            {
              q: 'Does it work with Looker Studio?',
              a: 'Yes — it publishes your data to a Google Sheet in your Drive and opens a Looker Studio report already bound to it with fields typed. There is no import format in Looker Studio; anything claiming otherwise is describing a menu that does not exist.',
            },
            {
              q: 'What happens if a scheduled refresh fails?',
              a: 'You are emailed, always — with what broke and what to do about it. A schedule that quietly stops is worse than no schedule, because no email reads as “nothing happened” rather than “we stopped looking”.',
            },
            {
              q: 'Can I invite my team?',
              a: 'Yes. Roles are owner, admin, member and viewer; a viewer can read everything and change nothing. Everything belongs to the workspace, so people can come and go without anything being lost.',
            },
            {
              q: 'Is my data separated from other customers?',
              a: 'Yes, at the database query itself rather than by a filter applied afterwards. A resource belonging to another workspace is reported as not found — not as forbidden, which would confirm it exists.',
            },
          ].map((entry) => (
            <Faq key={entry.q} question={entry.q}>
              {entry.a}
            </Faq>
          ))}
        </div>
      </section>

      {/* ---------------------------------------------------------- limits */}

      <section id="limits" className="guide-section">
        <h2>What it does not do</h2>
        <p className="lede">
          Stated plainly, because finding out later costs more than reading it now.
        </p>
        <ul className="limits">
          <li>
            <strong>No real-time streaming.</strong> Data refreshes on upload or on a schedule, not
            continuously.
          </li>
          <li>
            <strong>No Looker Studio import.</strong> Data goes out to Looker Studio; dashboards do not
            come back. Google exposes no API for reading a report's structure.
          </li>
          <li>
            <strong>No causal claims.</strong> The app reports correlations and trends. It will say two
            measures move together; it will never tell you one caused the other.
          </li>
          <li>
            <strong>The forecast band excludes future shocks.</strong> It covers the noise and the fit,
            not the chance that the trend breaks next month. Backtest coverage is the honest guide to
            how much to trust it.
          </li>
          <li>
            <strong>Sheets publishing has a size ceiling.</strong> Beyond a few tens of thousands of
            rows, send it to BigQuery and link that instead — the app will tell you when you hit it.
          </li>
        </ul>

        <div className="guide-cta">
          <h3>Ready to try it?</h3>
          <p>The first upload takes about a minute, and the free plan needs no card.</p>
          <button className="primary" onClick={onStart}>
            Add your first source
          </button>
        </div>
      </section>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Pieces
// -----------------------------------------------------------------------------

function Feature({
  glyph,
  name,
  summary,
  children,
  onOpen,
}: {
  glyph: string;
  name: string;
  summary: string;
  children: React.ReactNode;
  onOpen?: (() => void) | undefined;
}) {
  return (
    <article className="feature">
      <div className="feature-head">
        <span className="feature-glyph" aria-hidden>
          {glyph}
        </span>
        <div>
          <h3>{name}</h3>
          <p className="feature-summary">{summary}</p>
        </div>
        {onOpen !== undefined && (
          <button className="ghost tiny" onClick={onOpen}>
            Open →
          </button>
        )}
      </div>
      <div className="feature-body">{children}</div>
    </article>
  );
}

/** A callout for the mechanics — collapsed, because it is optional reading. */
function Behind({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={open ? 'behind open' : 'behind'}>
      <button className="behind-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="behind-mark" aria-hidden>
          {open ? '−' : '+'}
        </span>
        <span>{title}</span>
        <span className="behind-hint">behind the scenes</span>
      </button>
      {open && <div className="behind-body">{children}</div>}
    </div>
  );
}

function Walkthrough({
  who,
  goal,
  steps,
  outcome,
}: {
  who: string;
  goal: string;
  steps: readonly { did: string; saw: string; behind: string }[];
  outcome: string;
}) {
  return (
    <article className="walkthrough">
      <header>
        <h3>{who}</h3>
        <p className="muted">{goal}</p>
      </header>

      <ol className="walk-steps">
        {steps.map((step, index) => (
          <li key={index}>
            <div className="walk-did">{step.did}</div>
            <div className="walk-saw">
              <span className="walk-tag">You see</span>
              {step.saw}
            </div>
            <div className="walk-behind">
              <span className="walk-tag muted-tag">Behind it</span>
              {step.behind}
            </div>
          </li>
        ))}
      </ol>

      <footer className="walk-outcome">
        <strong>Outcome.</strong> {outcome}
      </footer>
    </article>
  );
}

function Quirk({ symptom, children }: { symptom: string; children: React.ReactNode }) {
  return (
    <div className="quirk">
      <h3>{symptom}</h3>
      <p>{children}</p>
    </div>
  );
}

function Faq({ question, children }: { question: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={open ? 'faq-item open' : 'faq-item'}>
      <button onClick={() => setOpen(!open)} aria-expanded={open}>
        <span>{question}</span>
        <span className="faq-mark" aria-hidden>
          {open ? '−' : '+'}
        </span>
      </button>
      {open && <p>{children}</p>}
    </div>
  );
}
